import React, { useState, useEffect, useMemo, useRef } from "react";
import SectionsDrawer from "./SectionsDrawer";
import { parseTimeGeneric } from "./scheduleAlgorithm";

// A simple collapsible component that animates the max-height.
const Collapsible = ({ isOpen, children }) => {
  const ref = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (ref.current) {
      setMaxHeight(isOpen ? `${ref.current.scrollHeight}px` : "0px");
    }
  }, [isOpen, children]);

  return (
    <div
      ref={ref}
      style={{ maxHeight, transition: "max-height 0.3s ease" }}
      className="overflow-hidden"
    >
      {children}
    </div>
  );
};

// Helpers for keys.
const courseKey = (course) => `${course.dept}-${course.number}`.toLowerCase();
const getCourseKey = (course) => {
  const dept = course.dept.toUpperCase();
  const number = course.number;
  const section = course.section ? course.section.toUpperCase() : "DEFAULT";
  return `${dept}_${number}_${section}`;
};

const CourseSection = ({
  plannedCourses = [],
  activeCourses = [],
  allCourseSchedules = [],
  isComputing,
  computeCombinations,
  combinations = [],
  combinationIndex,
  setCombinationIndex,
  onToggleCourse,
  onSelectAllCourses,
  hasComputed,
  isDirty,
  selectedSections,      // for tracking chosen section (or composite)
  setSelectedSections     // for updating chosen section
}) => {
  // Local state for row expansion and unified drawer management.
  const [expandedCourses, setExpandedCourses] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  // drawerData now holds { course, items, allItems } (allItems is the full list)
  const [drawerData, setDrawerData] = useState(null);
  const [drawerStep, setDrawerStep] = useState("main"); // "main" or "tutorial"
  const [pendingLecture, setPendingLecture] = useState(null);
  const [showDirectConflictAlert, setShowDirectConflictAlert] = useState(true);

  // Reset to main mode whenever the drawer opens.
  useEffect(() => {
    if (drawerOpen) {
      setDrawerStep("main");
      setPendingLecture(null);
    }
  }, [drawerOpen]);

  const colorOptions = [
    "bg-red-300",
    "bg-green-300",
    "bg-blue-300",
    "bg-yellow-300",
    "bg-purple-300",
    "bg-pink-300",
    "bg-indigo-300",
    "bg-teal-300",
    "bg-orange-300",
  ];
  const courseColors = {};
  plannedCourses.forEach((course, index) => {
    const key = courseKey(course);
    courseColors[key] = colorOptions[index % colorOptions.length];
  });

  function formatYearTerm(rawYear, rawTerm) {
    if (!rawYear || !rawTerm) return "";
    let year = rawYear;
    let term = rawTerm.toLowerCase();
    if (year === "registration" || year === "current") year = "2025";
    if (term === "registration" || term === "current") term = "fall";
    if (term.startsWith("spr")) term = "Spring";
    else if (term.startsWith("sum")) term = "Summer";
    else if (term.startsWith("fal")) term = "Fall";
    else if (term.startsWith("win")) term = "Winter";
    else term = "Unknown";
    return `${term} ${year}`;
  }

  const truncate = (str, maxLength) =>
    !str ? "" : str.length > maxLength ? str.substring(0, maxLength) + "..." : str;

  const allSelected =
    plannedCourses.length > 0 &&
    plannedCourses.every((course) =>
      activeCourses.some(
        (ac) =>
          ac.dept === course.dept &&
          ac.number === course.number &&
          ac.section === course.section
      )
    );

  const renderBadges = (info) => {
    if (!info) return null;
    return (
      <>
        {info.title && (
          <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
            {truncate(info.title, 25)}
          </span>
        )}
        {info.deliveryMethod && (
          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
            {info.deliveryMethod}
          </span>
        )}
        {info.units && (
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
            {info.units} Credits
          </span>
        )}
      </>
    );
  };

  const renderCourseInfo = (info) => (
    <dl className="text-sm text-gray-600 space-y-1">
      {info.title && (
        <>
          <dt className="font-semibold">Title:</dt>
          <dd>{info.title}</dd>
        </>
      )}
      {info.name && (
        <>
          <dt className="font-semibold">Course Name:</dt>
          <dd>{info.name}</dd>
        </>
      )}
      {info.term && (
        <>
          <dt className="font-semibold">Term:</dt>
          <dd>{info.term}</dd>
        </>
      )}
      {info.units && (
        <>
          <dt className="font-semibold">Units:</dt>
          <dd>{info.units}</dd>
        </>
      )}
      {info.deliveryMethod && (
        <>
          <dt className="font-semibold">Delivery Method:</dt>
          <dd>{info.deliveryMethod}</dd>
        </>
      )}
      {info.degreeLevel && (
        <>
          <dt className="font-semibold">Degree Level:</dt>
          <dd>{info.degreeLevel}</dd>
        </>
      )}
      {info.classNumber && (
        <>
          <dt className="font-semibold">Class Number:</dt>
          <dd>{info.classNumber}</dd>
        </>
      )}
      {info.dept && info.number && info.section && (
        <>
          <dt className="font-semibold">Course Identifier:</dt>
          <dd>
            {info.dept.toUpperCase()} {info.number} {info.section && info.section.toUpperCase()}
          </dd>
        </>
      )}
      {info.outlinePath && (
        <>
          <dt className="font-semibold">Outline Path:</dt>
          <dd>{info.outlinePath}</dd>
        </>
      )}
      {info.type && (
        <>
          <dt className="font-semibold">Type:</dt>
          <dd>{info.type}</dd>
        </>
      )}
      <dt className="font-semibold">Prerequisites:</dt>
      <dd>{info.prerequisites || "None"}</dd>
      <dt className="font-semibold">Corequisites:</dt>
      <dd>{info.corequisites || "None"}</dd>
      {info.description && (
        <>
          <dt className="font-semibold">Description:</dt>
          <dd>{info.description}</dd>
        </>
      )}
      {info.designation && (
        <>
          <dt className="font-semibold">Designation:</dt>
          <dd>{info.designation}</dd>
        </>
      )}
      {info.notes && (
        <>
          <dt className="font-semibold">Notes:</dt>
          <dd>{info.notes}</dd>
        </>
      )}
      {info.registrarNotes && (
        <>
          <dt className="font-semibold">Registrar Notes:</dt>
          <dd>
            <div
              className="prose"
              dangerouslySetInnerHTML={{ __html: info.registrarNotes }}
            />
          </dd>
        </>
      )}
      {info.requiredReadingNotes && (
        <>
          <dt className="font-semibold">Required Reading:</dt>
          <dd>
            <div
              className="prose"
              dangerouslySetInnerHTML={{ __html: info.requiredReadingNotes }}
            />
          </dd>
        </>
      )}
      {info.departmentalUgradNotes && (
        <>
          <dt className="font-semibold">Departmental Ugrad Notes:</dt>
          <dd>
            <div
              className="prose"
              dangerouslySetInnerHTML={{ __html: info.departmentalUgradNotes }}
            />
          </dd>
        </>
      )}
      {info.specialTopic && (
        <>
          <dt className="font-semibold">Special Topic:</dt>
          <dd>{info.specialTopic}</dd>
        </>
      )}
    </dl>
  );

  // Renders the selected section badge (or prompt) on the left column.
  const renderSelectedSection = (course) => {
    const key = getCourseKey(course);
    const selected = selectedSections ? selectedSections[key] : null;
    const fetchedCourse = allCourseSchedules.find(
      (cs) =>
        cs.dept === course.dept &&
        cs.number === course.number &&
        cs.section === course.section
    );
    const available = (fetchedCourse && fetchedCourse.availableSections) || [];
    if (selected) {
      return (
        <span
          className="inline-block w-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded cursor-pointer mt-1"
          onClick={() => {
            setDrawerData({ course, items: available, allItems: available });
            setDrawerStep("main");
            setDrawerOpen(true);
          }}
        >
          {selected.composite
            ? `${selected.lecture.section.toUpperCase()} + ${selected.tutorial.section.toUpperCase()}`
            : selected.section.toUpperCase()}
        </span>
      );
    } else {
      return (
        <span
          className="text-xs text-blue-600 underline cursor-pointer mt-1"
          onClick={() => {
            setDrawerData({ course, items: available, allItems: available });
            setDrawerStep("main");
            setDrawerOpen(true);
          }}
        >
          Select Section
        </span>
      );
    }
  };

  // Left column: displays course ID, term info, and the selected section badge.
  const renderLeftColumn = (course) => {
    const fetchedCourse = allCourseSchedules.find(
      (cs) =>
        cs.dept === course.dept &&
        cs.number === course.number &&
        cs.section === course.section
    );
    let yearTermText = "";
    if (fetchedCourse && fetchedCourse.sfuYear && fetchedCourse.fetchedTerm) {
      yearTermText = formatYearTerm(fetchedCourse.sfuYear, fetchedCourse.fetchedTerm);
    }
    return (
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-800">
          {course.dept.toUpperCase()} {course.number}{" "}
          {course.section && `(${course.section.toUpperCase()})`}
        </span>
        {yearTermText && <span className="text-xs text-gray-500">{yearTermText}</span>}
        {renderSelectedSection(course)}
      </div>
    );
  };

  // Toggle expanded state for course details.
  const toggleExpand = (course) => {
    const key = courseKey(course);
    setExpandedCourses((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Unified handler for section selection from the drawer.
  // In "main" mode:
  // • If a lecture with an associated class is chosen, check for tutorial options.
  // • If a tutorial is chosen directly, try to find its matching lecture from allItems.
  // In "tutorial" mode, combine the pending lecture and chosen tutorial.
  const handleDrawerSelect = (sec) => {
    const { course, items, allItems } = drawerData;
    if (drawerStep === "main") {
      // If a lecture with an associated class is chosen, check for matching tutorials.
      if ((sec.classType === "e" || sec.sectionCode === "LEC") && sec.associatedClass) {
        const tutorialOptions = items.filter(
          (s) =>
            (s.classType === "n" || s.sectionCode === "TUT") &&
            s.associatedClass === sec.associatedClass
        );
        if (tutorialOptions.length > 0) {
          setPendingLecture(sec);
          setDrawerStep("tutorial");
          // Replace the drawer items with the filtered tutorial options.
          setDrawerData({ course, items: tutorialOptions, allItems });
          return;
        }
      }
      // If a tutorial is selected directly in main mode, try to find its matching lecture.
      if ((sec.classType === "n" || sec.sectionCode === "TUT") && sec.associatedClass) {
        const matchingLecture = allItems.find(
          (s) =>
            (s.classType === "e" || s.sectionCode === "LEC") &&
            s.associatedClass === sec.associatedClass
        );
        if (matchingLecture) {
          const key = getCourseKey(course);
          const compositeSelection = {
            dept: course.dept,
            number: course.number,
            section: `${matchingLecture.section}+${sec.section}`,
            sfuData: {
              courseSchedule: [
                ...matchingLecture.sfuData.courseSchedule,
                ...sec.sfuData.courseSchedule,
              ],
              info: matchingLecture.sfuData.info,
            },
            composite: true,
            lecture: matchingLecture,
            tutorial: sec,
          };
          setSelectedSections((prev) => ({ ...prev, [key]: compositeSelection }));
          setDrawerOpen(false);
          if (computeCombinations) computeCombinations();
          return;
        }
      }
      // Otherwise, select the section directly.
      const key = getCourseKey(course);
      setSelectedSections((prev) => ({
        ...prev,
        [key]: { ...sec, dept: course.dept, number: course.number, section: sec.section || course.section },
      }));
      setDrawerOpen(false);
      if (computeCombinations) computeCombinations();
    } else if (drawerStep === "tutorial") {
      // In tutorial mode, combine pending lecture and selected tutorial.
      if (pendingLecture) {
        const key = getCourseKey(course);
        const mergedInfo = {
          ...pendingLecture.sfuData.info,
          description: `${pendingLecture.sfuData.info.description || ''} ${sec.sfuData.info?.description || ''}`.trim(),
        };
        const compositeSelection = {
          dept: course.dept,
          number: course.number,
          section: `${pendingLecture.section}+${sec.section}`,
          sfuData: {
            courseSchedule: [
              ...pendingLecture.sfuData.courseSchedule,
              ...sec.sfuData.courseSchedule,
            ],
            info: mergedInfo,
          },
          composite: true,
          lecture: pendingLecture,
          tutorial: sec,
        };
        setSelectedSections((prev) => ({ ...prev, [key]: compositeSelection }));
        setDrawerOpen(false);
        if (computeCombinations) computeCombinations();
      }      
    }
  };

  // Compute direct conflicts among active courses.
  const isConflict = (schedule1, schedule2) => {
    for (const m1 of schedule1) {
      if (m1.isExam) continue;
      const days1 = m1.days ? m1.days.split(",").map((d) => d.trim()) : [];
      const start1 = parseTimeGeneric(m1.startTime || "0:00");
      const end1 = parseTimeGeneric(m1.endTime || "0:00");
      for (const m2 of schedule2) {
        if (m2.isExam) continue;
        const days2 = m2.days ? m2.days.split(",").map((d) => d.trim()) : [];
        const start2 = parseTimeGeneric(m2.startTime || "0:00");
        const end2 = parseTimeGeneric(m2.endTime || "0:00");
        if (days1.some((day) => days2.includes(day))) {
          if (start1 < end2 && end1 > start2) return true;
        }
      }
    }
    return false;
  };

  const hasDirectConflict = useMemo(() => {
    const fixedActiveCourses = activeCourses.filter((course) => {
      const fetched = allCourseSchedules.find(
        (cs) =>
          cs.dept === course.dept &&
          cs.number === course.number &&
          cs.section === course.section
      );
      if (!fetched) return false;
      if (fetched.availableSections && fetched.availableSections.length > 1) return false;
      return (
        fetched.sfuData &&
        fetched.sfuData.courseSchedule &&
        fetched.sfuData.courseSchedule.length > 0
      );
    });

    for (let i = 0; i < fixedActiveCourses.length; i++) {
      const fetched1 = allCourseSchedules.find(
        (cs) =>
          cs.dept === fixedActiveCourses[i].dept &&
          cs.number === fixedActiveCourses[i].number &&
          cs.section === fixedActiveCourses[i].section
      );
      if (!fetched1 || !fetched1.sfuData || !fetched1.sfuData.courseSchedule) continue;
      for (let j = i + 1; j < fixedActiveCourses.length; j++) {
        const fetched2 = allCourseSchedules.find(
          (cs) =>
            cs.dept === fixedActiveCourses[j].dept &&
            cs.number === fixedActiveCourses[j].number &&
            cs.section === fixedActiveCourses[j].section
        );
        if (!fetched2 || !fetched2.sfuData || !fetched2.sfuData.courseSchedule) continue;
        if (isConflict(fetched1.sfuData.courseSchedule, fetched2.sfuData.courseSchedule)) return true;
      }
    }
    return false;
  }, [activeCourses, allCourseSchedules]);

  const allSelectedReady =
    plannedCourses.length > 0 &&
    plannedCourses.every((course) =>
      allCourseSchedules.some(
        (cs) =>
          cs.dept === course.dept &&
          cs.number === course.number &&
          cs.section === course.section
      )
    );

  return (
    <>
      <div className="w-full">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Courses</h2>
        <div className="p-1">
          {plannedCourses.length > 0 ? (
            <div>
              <div className="flex items-center mb-2">
                <input
                  id="select-all-courses"
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAllCourses(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="select-all-courses" className="ml-2 text-sm font-medium text-gray-700">
                  Select All Courses
                </label>
              </div>
              <div className="space-y-3 border-t border-gray-200 pt-2">
                {plannedCourses.map((course, idx) => {
                  const key = courseKey(course);
                  const isActive = activeCourses.some(
                    (ac) =>
                      ac.dept === course.dept &&
                      ac.number === course.number &&
                      ac.section === course.section
                  );
                  let primaryInfo = null;
                  const fetchedCourse = allCourseSchedules.find(
                    (cs) =>
                      cs.dept === course.dept &&
                      cs.number === course.number &&
                      cs.section === course.section
                  );
                  if (fetchedCourse) {
                    if (fetchedCourse.sfuData && fetchedCourse.sfuData.info) {
                      primaryInfo = fetchedCourse.sfuData.info;
                    } else if (
                      fetchedCourse.availableSections &&
                      fetchedCourse.availableSections.length > 0
                    ) {
                      const firstSec = fetchedCourse.availableSections[0];
                      if (firstSec.sfuData && firstSec.sfuData.info) {
                        primaryInfo = firstSec.sfuData.info;
                      }
                    }
                  }
                  let otherSections = [];
                  if (
                    fetchedCourse &&
                    fetchedCourse.availableSections &&
                    fetchedCourse.availableSections.length > 1
                  ) {
                    otherSections = fetchedCourse.availableSections.slice(1);
                  }
                  const leftColumn = renderLeftColumn(course);
                  const rightColumn = (
                    <div className="flex items-center space-x-2">
                      {primaryInfo ? (
                        renderBadges(primaryInfo)
                      ) : (
                        <span className="text-xs text-gray-500">
                          No additional course information
                        </span>
                      )}
                    </div>
                  );
                  return (
                    <div
                      key={idx}
                      className="border rounded shadow-sm p-3 bg-white overflow-x-hidden w-full"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => onToggleCourse(course)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
                          />
                          <span className={`inline-block w-4 h-4 rounded-full ${courseColors[key]}`}></span>
                          {leftColumn}
                        </div>
                        {rightColumn}
                        <button onClick={() => toggleExpand(course)} className="text-blue-600 ml-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className={`w-6 h-6 transform transition-transform duration-200 ${
                              expandedCourses[key] ? "rotate-180" : ""
                            }`}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </button>
                      </div>
                      <Collapsible isOpen={!!expandedCourses[key]}>
                        <div className="mt-2 border-t pt-2 w-full max-w-full whitespace-normal break-words">
                          <div className="max-h-64 overflow-y-auto">
                            {fetchedCourse && fetchedCourse.dataOriginAlert && (
                              <div className="bg-yellow-200 text-yellow-800 text-xs p-1 rounded mb-2">
                                {fetchedCourse.dataOriginAlert}
                              </div>
                            )}
                            {primaryInfo ? (
                              <div className="prose">{renderCourseInfo(primaryInfo)}</div>
                            ) : (
                              <div className="text-sm text-gray-500">No additional info found.</div>
                            )}
                          </div>
                          {otherSections.length > 0 && (
                            <div className="border-t pt-2">
                              <button
                                onClick={() => {
                                  setDrawerData({ course, items: otherSections, allItems: otherSections });
                                  setDrawerStep("main");
                                  setDrawerOpen(true);
                                }}
                                className="text-sm font-semibold text-blue-600 mb-2"
                              >
                                View Other Sections ({otherSections.length})
                              </button>
                            </div>
                          )}
                        </div>
                      </Collapsible>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700">Loading courses...</p>
          )}
        </div>

        {/* Conflict Alert */}
        {hasDirectConflict && showDirectConflictAlert && (
          <div
            id="alert-additional-content-2"
            className="p-4 mb-4 text-red-800 border border-red-300 rounded-lg bg-red-50"
            role="alert"
          >
            <div className="flex items-center">
              <svg
                className="shrink-0 w-4 h-4 me-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
              </svg>
              <span className="sr-only">Info</span>
              <h3 className="text-lg font-medium">Schedule Conflict Detected</h3>
            </div>
            <div className="mt-2 mb-4 text-sm">
              Some of the selected courses have fixed schedules that conflict with each other and cannot be taken together.
              Please review your course selections.
            </div>
            <div className="flex">
              <button
                type="button"
                className="text-white bg-red-800 hover:bg-red-900 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-xs px-3 py-1.5 inline-flex items-center me-2"
              >
                <svg
                  className="me-2 h-3 w-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 14"
                >
                  <path d="M10 0C4.612 0 0 5.336 0 7c0 1.742 3.546 7 10 7 6.454 0 10-5.258 10-7 0-1.664-4.612-7-10-7Zm0 10a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                </svg>
                View Details
              </button>
              <button
                type="button"
                className="text-red-800 bg-transparent border border-red-800 hover:bg-red-900 hover:text-white focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-xs px-3 py-1.5"
                onClick={() => setShowDirectConflictAlert(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Generate Schedules Button */}
        <div className="mt-4">
          <button
            onClick={computeCombinations}
            className={`inline-flex items-center text-white ${
              isDirty ? "bg-gray-600 hover:bg-gray-700" : "bg-blue-700 hover:bg-blue-800"
            } focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2.5`}
            disabled={isComputing || !allSelectedReady}
          >
            {isComputing ? (
              <>
                <svg
                  aria-hidden="true"
                  role="status"
                  className="inline w-4 h-4 mr-3 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
                Generating Schedules...
              </>
            ) : (
              isDirty ? "Regenerate Schedule" : "Generate Schedules"
            )}
          </button>
        </div>
      </div>

      {/* Unified Sections Drawer */}
      {drawerOpen && drawerData && (
        <SectionsDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={
            drawerStep === "main"
              ? `Other Sections for ${drawerData.course.dept.toUpperCase()} ${drawerData.course.number} ${
                  drawerData.course.section ? "(" + drawerData.course.section.toUpperCase() + ")" : ""
                }`
              : `Select Tutorial for ${drawerData.course.dept.toUpperCase()} ${drawerData.course.number} (Lecture: ${pendingLecture.section.toUpperCase()})`
          }
          items={drawerData.items}
          onSelectItem={handleDrawerSelect}
          renderItemContent={(sec) =>
            sec.sfuData && sec.sfuData.info ? (
              renderCourseInfo(sec.sfuData.info)
            ) : (
              <div className="text-gray-600">No info.</div>
            )
          }
          selectButtonText={drawerStep === "main" ? "Select" : "Select Tutorial"}
          footer={
            drawerStep === "main" ? (
              <div className="border rounded p-2 text-center">
                <button
                  onClick={() => {
                    const key = getCourseKey(drawerData.course);
                    setSelectedSections((prev) => {
                      const newSelections = { ...prev };
                      delete newSelections[key];
                      return newSelections;
                    });
                    setDrawerOpen(false);
                    if (typeof computeCombinations === "function") {
                      computeCombinations();
                    }
                  }}
                  className="mt-2 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Use All Sections
                </button>
              </div>
            ) : (
              <div className="border rounded p-2 flex justify-between items-center">
                <div className="text-sm">Skip Tutorial</div>
                <button
                  onClick={() => {
                    const key = getCourseKey(drawerData.course);
                    setSelectedSections((prev) => ({
                      ...prev,
                      [key]: { ...drawerData.course, ...pendingLecture },
                    }));
                    setDrawerOpen(false);
                    if (computeCombinations) computeCombinations();
                  }}
                  className="mt-2 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Skip
                </button>
              </div>
            )
          }
        />
      )}
    </>
  );
};

export default CourseSection;